import { FormEventHandler } from "react";
import { useSelector } from "react-redux";
import { Field } from "redux-form/immutable";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Link,
  Stack,
  Text
} from "@seneca/senekit";

import { ErrorMessage as ErrorMessageType } from "services/firebase/auth/errorTranslator";

import { MultiLineTextPlaceholder } from "features/common/components/TextPlaceholder";
import useAppName from "features/common/hooks/useAppName";
import { isMobile } from "features/common/utils/functions/mobile";
import { FormTextInput } from "features/forms/components/FormTextInput";
import { getIncludePhoneNumber } from "features/regions/selectors";
import { ErrorMessage } from "features/sign-up/common-routes/components/form";
import { useTermsAndConditionsLinkByVariant } from "features/sign-up/hooks";

import { PASSWORD_FIELD_NAME, USERNAME_FIELD_NAME } from "../../../const";
import MarketingEmailsCheckbox from "../components/MarketingEmailsCheckbox/MarketingEmailsCheckbox";
import useShowMarketingEmailsCheckboxByVariant from "../components/MarketingEmailsCheckbox/useShowMarketingEmailsCheckboxByVariant";

const mobile = isMobile();

type Props = {
  handleSubmit: FormEventHandler;
  submitting?: boolean;
  error?: ErrorMessageType;
  disableEmail?: boolean;
  loading?: boolean;
  showPrefillInfo?: boolean;
};

export default function SignUpFormEmail({
  handleSubmit,
  submitting,
  error,
  disableEmail,
  loading,
  showPrefillInfo
}: Props) {
  const appName = useAppName();
  const includePhoneNumber = useSelector(getIncludePhoneNumber);
  const termsAndConditionsLink = useTermsAndConditionsLinkByVariant();
  const showMarketingEmailsCheckbox = useShowMarketingEmailsCheckboxByVariant();

  return (
    <Box component="form" onSubmit={handleSubmit} width="100%">
      <Stack space="xlarge">
        <MultiLineTextPlaceholder
          loading={loading}
          placeholderWidth="100%"
          placeholderHeight="50px"
          numberOfLines={4}
        >
          <Field
            name="firstName"
            component={FormTextInput}
            type="text"
            label="First name"
            autoFocus={!mobile}
          />

          <Field
            name="lastName"
            component={FormTextInput}
            type="text"
            label="Last name"
          />
          <Field
            name={USERNAME_FIELD_NAME}
            component={FormTextInput}
            type="email"
            label="Email"
            autoComplete="off"
            disabled={disableEmail}
          />
          {showPrefillInfo && (
            <Box marginY="xsmall">
              <Alert tone="primary" iconName="info">
                Signing up with this email ensures you will be connected to your
                child's account. You can change your email later from your
                settings.
              </Alert>
            </Box>
          )}
          {includePhoneNumber && (
            <Field
              name="phoneNumber"
              component={FormTextInput}
              type="phoneNumber"
              label="Mobile Number"
            />
          )}

          <Field
            name={PASSWORD_FIELD_NAME}
            component={FormTextInput}
            type="password"
            label="Password"
          />
        </MultiLineTextPlaceholder>

        <Field
          name="termsAndConditions"
          component={TermsAndConditionsCheckbox}
          appName={appName}
          link={termsAndConditionsLink}
        />

        {showMarketingEmailsCheckbox && (
          <Field
            name="isSubscribedToMarketingEmails"
            component={MarketingEmailsCheckboxField}
          />
        )}

        {error && <ErrorMessage errorMessage={error} />}

        <Button onClick={() => {}} loading={submitting} width="100%">
          Sign up with email
        </Button>
      </Stack>
    </Box>
  );
}

type CheckboxFieldInput = {
  input: { name: string; value: boolean; onChange: () => void };
  meta: { error: string; submitFailed: boolean };
};

type TermsAndConditionsProps = {
  appName: string;
  link: string;
} & CheckboxFieldInput;

function TermsAndConditionsCheckbox(props: TermsAndConditionsProps) {
  const {
    appName,
    link,
    input: { name, value, onChange },
    meta: { error, submitFailed }
  } = props;
  return (
    <Stack space="large">
      <Checkbox name={name} checked={value} onChange={onChange}>
        <Text size="small" tone="neutral">
          I agree to {appName}'s <Link to={link}>Terms and Conditions</Link>
        </Text>
      </Checkbox>
      {submitFailed && error && (
        <Text size="small" tone="error">
          You must agree to the terms and conditions to use {appName}
        </Text>
      )}
    </Stack>
  );
}

function MarketingEmailsCheckboxField(props: CheckboxFieldInput) {
  const {
    input: { name, value, onChange }
  } = props;
  return (
    <MarketingEmailsCheckbox name={name} checked={value} onChange={onChange} />
  );
}
